import { OrderCompleteV3Query } from '~/__generated__/app/query/order-complete/order-complete.graphql'
import { CANCELLATION_STATUS, SERVICE_TYPE, STATUS } from '~/app/utils/OrderListUtils'
import {
  CartBusOrderReservationTicketStatus,
  OrderDetailsBusReservationTicketStatus,
  CategoryType,
} from '~/__generated__/__types__'
import { DateTime } from 'luxon'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import conf from '~/app/conf'
import { OrderListV3Query } from '~/__generated__/app/query/order-list/order-list.graphql'
import { ConvertedOrder } from '~/app/components/order-converter/getCartOrderConverter'
import { toTimeWithPlusDay } from '~/app/utils/timeFormat'
import { busGradeText, busTicketText, removeFalsyAndJoin } from '~/app/utils/filters'
import { SharedOrderQuery } from '~/__generated__/app/query/order-shared/order-shared-list.graphql'
import { SharedOrderProps } from '~/app/components/order-share/SharedOrderListCardItem'
import { OrderDetailsV3Query } from '~/app/query/order-details/order-details.graphql'
import {
  OrderDetailsBusReservationTicket,
  SharedOrderBusReservationTicket,
} from '~/__generated__/__types__'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { CancellationCardItemOrder } from '~/app/components/order-list/details/CancelInformation/CancellationCardItem'
import { Props as OrderCartItemProp } from '~/app/components/order-list/common/OrderCardItem'
import { dateWithDayOfWeek } from '~/app/utils/datetime'
import { IBrazePurchaseEventLog, Category, Currency } from '~/app/braze/brazeCustomLog'
import {
  getOrderCompleteBrazeCouponAndPointInfo,
  getBrazeOrderCategory,
  IConverterBrazeCompositeOrderComplete,
} from '~/app/components/order-converter/useOrderCompleteBrazeCustomConverter'
import { IFbqPurchaseData } from '~/app/pixel'

import { periodWithDayOrWeekFormat } from '~/api/utils/datetime'
import {
  KinesisLogMetaId,
  OrderButton,
  OrderButtonIconType,
  OrderButtonType,
} from '~/app/components/order-converter/types'

type OrderCompleteCategoryOrder = OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number]
export type OrderCompleteBus = Extract<
  OrderCompleteCategoryOrder,
  { __typename?: 'OrderDetailsBus' }
>
type OrderCompleteBusReservation = OrderCompleteBus['reservations'][number]

type OrderDetailBus = Extract<
  OrderDetailsV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsBus' }
>
type OrderDetailBusReservation = OrderDetailBus['reservations'][number]

type CartBusOrder = Extract<
  OrderListV3Query['orderListV3']['orders'][number]['categoryOrders'][number],
  { __typename?: 'CartBusOrder' }
>
type CartBusOrderReservation = CartBusOrder['reservations'][number]
type Reservation = CartBusOrderReservation
type Ticket = Reservation['tickets'][number] | OrderDetailBusReservation['tickets'][number]

export type OrderDetailsCancellationBusOrder = Extract<
  OrderDetailsCancellationCategoryOrders[number],
  { __typename?: 'OrderDetailsCancellationBus' }
>
type CancellationReservations = OrderDetailsCancellationBusOrder['reservations']
type CancellationReservation = OrderDetailsCancellationBusOrder['reservations'][number]

export type SharedOrderBus = Extract<
  SharedOrderQuery['sharedOrder']['categoryOrders'][number],
  { __typename?: 'SharedOrderBus' }
>
type SharedOrderBusReservation = SharedOrderBus['reservations'][number]

export type CommonBusParameter =
  | OrderDetailBusReservation
  | Reservation
  | SharedOrderBusReservation
  | CancellationReservation
  | OrderCompleteBusReservation

type GetBusInfo = {
  busGrade: string
  ticket:
    | OrderDetailBusReservation['tickets'][number]
    | Reservation['tickets'][number]
    | SharedOrderBusReservation['tickets'][number]
    | CancellationReservation['tickets'][number]
    | OrderCompleteBusReservation['tickets'][number]
}

export const getBusImage = (busType?: string, isSquare?: boolean) => {
  return isSquare
    ? (conf.busLogoImage.square?.[busType ?? ''] ?? '')
    : (conf.busLogoImage.wide?.[busType ?? ''] ?? '')
}

export const getBusSubTitle = ({
  departureTime,
  departureDate,
  expectedArriveAt,
}: CommonBusParameter) => {
  // 기차, 버스 통일된 시간 포맷을 위해 공용 함수 사용
  return periodWithDayOrWeekFormat({
    from: { date: departureDate, time: departureTime },
    to: expectedArriveAt,
  })
}

export const getBusInfo = ({ busGrade, ticket }: GetBusInfo) => {
  const busGradeValue = busGradeText(busGrade) ?? ''
  const seatNoText = `${ticket.seatNo}번 좌석`
  const passengers = `${busTicketText(ticket.ticketType)} 1`

  return [[busGradeValue, seatNoText, passengers]]
}

export const toOrderCompleteProductBus = (busOrder: OrderCompleteBus) => {
  return busOrder.reservations.reduce<OrderCartItemProp[]>((acc, reservation) => {
    const {
      transportationCompanyCode,
      departureTerminalName,
      arrivalTerminalName,
      busGrade,
      expectedArriveAt,
    } = reservation
    const ticketOrders = reservation.tickets.map(ticket => ({
      id: busOrder.id,
      categoryOrderId: busOrder.id,
      serviceType: SERVICE_TYPE.BUS,
      imageSrc: getBusImage(transportationCompanyCode, true),
      status: STATUS.READY, // 주문완료 화면에서는 상태를 표현하지 않는다STATUS.READY // 주문완료 화면에서는 상태를 표현하지 않는다
      title: `${departureTerminalName} - ${arrivalTerminalName}`,
      subTitle: getBusSubTitle(reservation),
      info: getBusInfo({ busGrade, ticket }),
      busTransportationCompanyCode: transportationCompanyCode,
      categoryType: CategoryType.DomesticBus,
      useEndDate: isoToFormat(expectedArriveAt, 'yyyy-MM-dd'),
      cancelPolicySummary: ticket.cancelPolicySummary,
    }))
    return acc.concat(ticketOrders)
  }, [])
}

export const getBusButtons = ({
  status,
  props,
  transportationCompanyCode,
  categoryOrderId,
  kinesisLogMetaId,
}: {
  status: STATUS
  props: Ticket
  transportationCompanyCode: string
  categoryOrderId: string
  kinesisLogMetaId: KinesisLogMetaId
}) => {
  const buttons: Array<OrderButton> = []

  if (status === STATUS.READY) {
    buttons.push(
      {
        type: OrderButtonType.BASIC,
        title: '예약 취소',
        id: 'cancel',
        isDisabled: false,
        data: {
          id: props.id,
          categoryOrderId,
        },
      },
      {
        type: OrderButtonType.BASIC,
        title: '이용 정보',
        id: 'information',
        data: { type: 'bus', transportationCompanyCode, categoryOrderId },
      }
    )
    buttons.push({
      type: OrderButtonType.PRIMARY,
      title: '승차권 QR 보기',
      id: 'check-bus-tickets',
      icon: OrderButtonIconType.QR,
      data: { ...props, kinesisLogMeta: { ...kinesisLogMetaId, productType: 'bus' } },
    })
  }

  return buttons
}

export const getBusStatus = (
  status: CartBusOrderReservationTicketStatus | OrderDetailsBusReservationTicketStatus,
  expectedArriveAt: string,
  currentAt?: string
) => {
  const now = currentAt ? DateTime.fromISO(currentAt) : DateTime.now()
  return status === CartBusOrderReservationTicketStatus.Canceled ||
    status === CartBusOrderReservationTicketStatus.Aborted
    ? STATUS.CANCEL_COMPLETED
    : status === CartBusOrderReservationTicketStatus.Reserved
      ? DateTime.fromISO(expectedArriveAt).diff(now, ['second']).seconds < 0
        ? STATUS.COMPLETED
        : STATUS.READY
      : STATUS.READY
}
export const toBusOrder =
  (categoryOrderId: string, currentAt: string, kinesisLogMetaId: KinesisLogMetaId) =>
  (
    acc: ConvertedOrder[],
    reservation: OrderDetailBusReservation,
    _i,
    reservations: OrderDetailBusReservation[]
  ) => {
    const {
      transportationCompanyCode,
      departureDate,
      departureTerminalNo,
      departureTerminalName,
      arrivalTerminalNo,
      arrivalTerminalName,
      tickets,
      expectedArriveAt,
      busGrade,
    } = reservation

    // 고속버스 web 개발 제외로 pdpUrl 사용하지 않음.
    const deeplink =
      `${conf.deeplink.scheme.bus}` +
      `?departureCode=${departureTerminalNo}` +
      `&departureName=${encodeURIComponent(departureTerminalName)}` +
      `&arrivalCode=${arrivalTerminalNo}` +
      `&arrivalName=${encodeURIComponent(arrivalTerminalName)}` +
      `&departureDate=${departureDate}` +
      `&arrivalDate=${isoToFormat(expectedArriveAt, 'yyyy-MM-dd')}` +
      `&tripType=${reservations.length === 2 ? 'round' : 'oneway'}`

    // 고속버스는 승차권:좌석 = 1:1. 즉, 좌석 단위로 노출함
    const ticketOrders = tickets.map((ticket: Ticket) => {
      // 승차권 QR 검표 or 수동검표 완료된 경우 이용완료로 노춣함
      const confirm: boolean =
        ticket.__typename === 'OrderDetailsBusReservationTicket'
          ? ticket.confirm
          : ticket.__typename === 'CartBusOrderReservationTicket'
            ? ticket.confirmed
            : false
      const status = confirm
        ? STATUS.COMPLETED
        : getBusStatus(ticket.status, expectedArriveAt, currentAt)
      return {
        id: String(ticket.id),
        status,
        serviceType: SERVICE_TYPE.BUS,
        categoryType: CategoryType.DomesticBus,
        imageSrc: getBusImage(transportationCompanyCode, true),
        title: `${departureTerminalName} - ${arrivalTerminalName}`,
        subTitle: getBusSubTitle(reservation),
        info: getBusInfo({ busGrade, ticket }),
        buttons: getBusButtons({
          status,
          props: ticket,
          transportationCompanyCode,
          categoryOrderId,
          kinesisLogMetaId,
        }),
        price: ticket.__typename === 'OrderDetailsBusReservationTicket' ? ticket.price : undefined, // 주문 내역에서는 노출되지 않음.
        categoryOrderId,
        deeplink,
        busTransportationCompanyCode: transportationCompanyCode,
        useEndDate: isoToFormat(expectedArriveAt, 'yyyy-MM-dd'),
        cancelPolicySummary: ticket.cancelPolicySummary,
      }
    })

    return acc.concat(ticketOrders)
  }

export const toCartBusOrder = (
  { reservations, id }: OrderDetailBus,
  currentAt: string,
  kinesisLogMetaId: KinesisLogMetaId
) => {
  const convertBusOrder = toBusOrder(id, currentAt, kinesisLogMetaId)
  return reservations.reduce<ConvertedOrder[]>(convertBusOrder, [])
}

export const toSharedBusOrder = ({ reservations, id }: SharedOrderBus) => {
  return reservations.reduce<SharedOrderProps[]>((acc, reservation) => {
    const { transportationCompanyCode, departureTerminalName, arrivalTerminalName, busGrade } =
      reservation
    const ticketOrders = reservation.tickets.map(ticket => ({
      serviceType: SERVICE_TYPE.BUS,
      categoryType: CategoryType.DomesticBus,
      imageSrc: getBusImage(transportationCompanyCode, true),
      title: `${departureTerminalName} - ${arrivalTerminalName}`,
      subTitle: getBusSubTitle(reservation),
      info: getBusInfo({ busGrade, ticket }),
      id: String(ticket.id),
      categoryOrderId: id,
    }))
    return acc.concat(ticketOrders)
  }, [])
}

export const toBusTicketCards = (
  ticketId: string,
  reservations: (SharedOrderBusReservation | OrderDetailBusReservation)[]
) => {
  const reservation = reservations.find(reservation =>
    reservation.tickets.some(ticket => ticket.id === +ticketId)
  )
  if (reservation) {
    return (
      reservation.tickets as (SharedOrderBusReservationTicket | OrderDetailsBusReservationTicket)[]
    )
      .filter(ticket => ticket.status === OrderDetailsBusReservationTicketStatus.Reserved)
      .map(ticket => {
        const [hour, minutes] = reservation.departureTime.split(':')
        return {
          id: ticket.id,
          departureDate: reservation.departureDate,
          departureDateText: dateWithDayOfWeek(reservation.departureDate),
          logoImageSrc: getBusImage(reservation.transportationCompanyCode),
          busGradeText: busGradeText(reservation.busGrade),
          departureTime: `${hour}:${minutes}`,
          departureTerminalName: reservation.departureTerminalName,
          arrivalTime: toTimeWithPlusDay({
            from: { date: reservation.departureDate, time: reservation.departureTime },
            to: reservation.expectedArriveAt,
          }),
          arrivalTerminalName: reservation.arrivalTerminalName,
          boardingNo: reservation?.boardingNo ?? '-',
          seatNo: ticket.seatNo,
          ticketType: ticket.ticketType,
          ticketTypeText: busTicketText(ticket.ticketType),
          isConfirm: ticket.confirm,
          routerTicketNo:
            ticket.__typename === 'OrderDetailsBusReservationTicket' ? ticket.routerTicketNo : null,
          qrGenNo: ticket.__typename === 'OrderDetailsBusReservationTicket' ? ticket.qrGenNo : null,
          transportationCompanyCode: reservation.transportationCompanyCode,
        }
      })
  } else {
    return []
  }
}

export const toCancellationBusOrder = (
  reservations: CancellationReservations,
  categoryOrderId: string
) => {
  return reservations.reduce<CancellationCardItemOrder[]>((acc, reservation) => {
    const busTickets = reservation.tickets.map(ticket => ({
      id: String(ticket.id),
      categoryOrderId,
      serviceType: SERVICE_TYPE.TRANSPORTATION,
      status: CANCELLATION_STATUS.COMPLETED,
      imageSrc: getBusImage(reservation.transportationCompanyCode, true),
      refundPrice: ticket.price,
      commissionAmount: ticket.commissionAmount,
      title: `${reservation.departureTerminalName} - ${reservation.arrivalTerminalName}`,
      subTitle: getBusSubTitle(reservation),
      info: getBusInfo({ busGrade: reservation.busGrade, ticket }),
    }))
    return acc.concat(busTickets)
  }, [])
}

export const getFilteredShareBusOrder = (
  sharedReservations: SharedOrderBusReservation[],
  currentAt: string
): { reservations: SharedOrderBusReservation[]; reservationTicketCount: number } => {
  const reservations = sharedReservations.map(reservation => {
    const tickets = reservation.tickets.filter(ticket => {
      // 승차권 QR 검표 or 수동검표 완료된 경우 노출하지 않음.
      if (ticket.confirm) {
        return false
      }
      // 도착예정시간 지난 경우 노출하지 않음.
      if (ticket.status === OrderDetailsBusReservationTicketStatus.Reserved) {
        const isExpired =
          DateTime.fromISO(reservation.expectedArriveAt)
            .diff(DateTime.fromISO(currentAt))
            .toMillis() < 0
        return !isExpired
      }
      return false
    })
    return {
      ...reservation,
      tickets,
    }
  })
  const reservationTicketCount = reservations.reduce<number>((count, reservation) => {
    count += reservation.tickets.length
    return count
  }, 0)
  return { reservations, reservationTicketCount }
}

export const toOrderCompleteBrazeCustomBus = ({
  order,
  orderDetails,
  isNolPayment,
}: {
  order: OrderCompleteBus
  orderDetails: OrderCompleteV3Query['orderDetailsV3']
  isNolPayment: boolean
}): IConverterBrazeCompositeOrderComplete => {
  const { category } = order
  const departure = order.reservations[0]
  const arrival = order.reservations[1]
  const {
    departureTerminalNo,
    arrivalTerminalNo,
    departureTerminalName,
    arrivalTerminalName,
    departureAt,
  } = departure
  const arrivalAt = arrival?.departureAt
  const { createdAt, id } = orderDetails
  const formatDate = date => DateTime.fromISO(date).toFormat('yyyy-MM-dd')
  const isSameDate = formatDate(createdAt) === formatDate(departureAt)
  const isAfterDate =
    (DateTime.fromISO(departureAt).diff(DateTime.fromISO(createdAt), 'days').toObject().days || 0) >
    0

  const tickets = order.reservations.map(reservation => reservation.tickets).flat()
  const has_children = tickets.some(ticket => ticket.ticketType === '2')
  const price = tickets.reduce((total, ticket) => total + ticket.price, 0)

  const data: IConverterBrazeCompositeOrderComplete = {
    category: getBrazeOrderCategory(order),
    composite_order_id: id,
    property_category: category,
    item_id: `${departureTerminalNo}, ${arrivalTerminalNo}`,
    item_name: `${departureTerminalName}, ${arrivalTerminalName}`,
    check_in_date: DateTime.fromISO(departureAt).toISO(),
    is_earlybird: !isSameDate && isAfterDate,
    cust_count: tickets.length,
    has_children,
    ...getOrderCompleteBrazeCouponAndPointInfo({
      orderDetails,
      coupon: order.busDiscount?.coupons[0],
      price,
    }),
    nolcard_approval: isNolPayment,
    city_name: removeFalsyAndJoin([departureTerminalName, arrival?.departureTerminalName]),
  }

  if (arrivalAt) {
    data.check_out_date = DateTime.fromISO(arrivalAt).toISO()
  }

  return data
}

export const getOrderCompleteFacebookPixelBusData = ({
  order,
}: {
  order: OrderCompleteBus
}): IFbqPurchaseData => {
  const departure = order.reservations[0]
  const arrival = order.reservations[1]

  const { num_adults, num_children } = departure.tickets.reduce(
    (acc, seat) => {
      if (seat.ticketType === '1' || seat.ticketType === '8') {
        return { ...acc, num_adults: acc.num_adults + 1 }
      } else {
        return { ...acc, num_children: acc.num_children + 1 }
      }
    },
    { num_adults: 0, num_children: 0 }
  )
  const tickets = order.reservations.map(reservation => reservation.tickets).flat()
  const price = tickets.reduce((total, ticket) => total + ticket.price, 0)

  const data: IFbqPurchaseData = {
    content_type: 'd_bus',
    content_ids: removeFalsyAndJoin([departure.departureTerminalNo, arrival?.departureTerminalNo]),
    city: removeFalsyAndJoin([departure.departureTerminalName, arrival?.departureTerminalName]),
    region: removeFalsyAndJoin([departure.departureTerminalName, arrival?.departureTerminalName]),
    num_adults,
    num_children,
    value: price,
    currency: 'KRW',
    checkin_date: DateTime.fromISO(departure.departureAt).toISO(),
  }

  if (arrival?.departureAt) {
    data.checkout_date = DateTime.fromISO(arrival.departureAt).toISO()
  }
  return data
}

export const toReceiptBus = ({ reservations, id }: OrderDetailBus) => {
  return reservations.reduce<ConvertedOrder[]>((acc, reservation) => {
    const {
      transportationCompanyCode,
      departureTerminalName,
      arrivalTerminalName,
      tickets,
      expectedArriveAt,
      busGrade,
    } = reservation

    const ticketOrders = tickets.map((ticket: Ticket) => {
      // 승차권 QR 검표 or 수동검표 완료된 경우 이용완료로 노춣함
      const confirm: boolean =
        ticket.__typename === 'OrderDetailsBusReservationTicket'
          ? ticket.confirm
          : ticket.__typename === 'CartBusOrderReservationTicket'
            ? ticket.confirmed
            : false
      return {
        id: `${ticket.id}`,
        status: confirm ? STATUS.COMPLETED : getBusStatus(ticket.status, expectedArriveAt),
        title: `${departureTerminalName} - ${arrivalTerminalName}`,
        subTitle: getBusSubTitle(reservation),
        serviceType: SERVICE_TYPE.BUS,
        categoryOrderId: id,
        imageSrc: getBusImage(transportationCompanyCode, true),
        info: getBusInfo({ busGrade, ticket }),
        categoryType: CategoryType.DomesticBus,
        price: ticket.__typename === 'OrderDetailsBusReservationTicket' ? ticket.price : undefined,
      }
    })

    return acc.concat(ticketOrders)
  }, [])
}

export const toOrderCompleteBrazePurchaseBus = (
  busOrder: OrderCompleteBus,
  payments: OrderCompleteV3Query['orderDetailsV3']['payments'],
  paid_amount: number
): IBrazePurchaseEventLog => {
  const departure = busOrder.reservations[0]
  const arrival = busOrder.reservations[1]
  const { departureTerminalNo, arrivalTerminalNo, tickets } = departure
  const { price } = tickets[0]
  const { price: arrivalPrice = 0 } = arrival?.tickets[0] || {}
  const payment_method = payments.map(payment => payment?.paymentMethod?.code || '')

  return {
    productId: `${departureTerminalNo}, ${arrivalTerminalNo}`,
    price: price + arrivalPrice,
    currencyCode: Currency.KRW,
    purchaseProperties: {
      category: Category.BUS,
      payment_method,
      paid_amount,
    },
  }
}
